import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight } from "lucide-react";

const categoryLabels = {
  marche: "Marchés", foire: "Foires", salon: "Salons",
  festival: "Festivals", exposition: "Expositions", autre: "Événements",
};

const regionTags = {
  "Île-de-France": "IDF",
  "Auvergne-Rhône-Alpes": "ARA",
  "Provence-Alpes-Côte d'Azur": "PACA",
  "Nouvelle-Aquitaine": "NAQ",
  "Occitanie": "OCC",
  "Hauts-de-France": "HDF",
  "Grand Est": "GES",
  "Bretagne": "BRE",
  "Pays de la Loire": "PDL",
  "Normandie": "NOR",
  "Bourgogne-Franche-Comté": "BFC",
  "Centre-Val de Loire": "CVL",
  "Corse": "COR",
};

export default function RegionExplorer() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/regions/stats").then((r) => {
      setStats(r.data);
      setLoading(false);
    });
  }, []);

  const buildHeadline = (s) => {
    const cat = s.categories[0];
    const label = cat ? categoryLabels[cat] || "Événements" : "Événements";
    return `${label} en ${s.region}`;
  };

  return (
    <section
      data-testid="region-explorer"
      className="bg-ink text-cream py-28 grain"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-16 gap-4">
          <div>
            <p className="text-label text-accent mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-accent" /> Explorer la France
            </p>
            <h2 className="font-heading font-light tracking-editorial text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Région par <span className="font-display italic text-accent">région</span>.
            </h2>
          </div>
          <p className="text-sm text-cream/50 max-w-xs font-light leading-relaxed">
            Les 13 régions françaises. Cliquez pour découvrir tous les événements
            soigneusement sélectionnés près de chez vous.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-px bg-cream/10">
          {loading
            ? Array.from({ length: 13 }).map((_, i) => (
                <div key={i} className="h-36 bg-ink animate-pulse" />
              ))
            : stats.map((s) => {
                const active = s.events > 0 || s.venues > 0;
                return (
                  <Link
                    key={s.region}
                    to={`/events?region=${encodeURIComponent(s.region)}`}
                    data-testid={`region-${regionTags[s.region]}`}
                    className={`group relative p-6 bg-ink transition-all duration-300 ${
                      active
                        ? "hover:bg-accent cursor-pointer"
                        : "opacity-30 cursor-not-allowed pointer-events-none"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className="font-mono text-[10px] tracking-[0.25em] text-cream/40 group-hover:text-cream transition-colors">
                        {regionTags[s.region]}
                      </span>
                      {active && (
                        <ArrowRight className="h-3 w-3 text-cream/30 group-hover:text-cream group-hover:translate-x-0.5 transition-all" />
                      )}
                    </div>
                    <p className="font-heading font-light text-xl leading-tight tracking-editorial mb-3 group-hover:text-white transition-colors">
                      {active ? buildHeadline(s) : s.region}
                    </p>
                    {active && (
                      <p className="text-[11px] text-cream/40 group-hover:text-cream/70 font-light">
                        {s.events > 0 && (
                          <span data-testid={`region-count-${regionTags[s.region]}`}>
                            {s.events} événement{s.events > 1 ? "s" : ""}
                          </span>
                        )}
                        {s.events > 0 && s.venues > 0 && " · "}
                        {s.venues > 0 && (
                          <span>{s.venues} lieu{s.venues > 1 ? "x" : ""}</span>
                        )}
                      </p>
                    )}
                    {!active && (
                      <p className="text-[11px] text-cream/30 font-light italic">Bientôt</p>
                    )}
                  </Link>
                );
              })}
        </div>
      </div>
    </section>
  );
}
